import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import Modal from '../DireccionesUnidadesOrg/DireccionesUnidadesOrgModal';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const TITLETAB = 'Direcciones';
const idKey = 'idDireccion';
const endpointBase = ENDPOINTS.direccionesUnidadesOrg;
const endpointUnitById = ENDPOINTS.unidadesOrgById;
const endpointUnitPut = ENDPOINTS.unidadesOrg;
const normalizeDir = Models.direccionesUnidadesOrg;
const normalizeUnit = Models.unidadesOrg;

export default function UnidadesOrgDireccionesTab({ fkValue, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [unitData, setUnitData] = useState(null);
  const [linkedAddress, setLinkedAddress] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('new');
  const [editData, setEditData] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setFormOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Obtener datos de la unidad para ver qué ID de dirección tiene
      const resUnit = await axios.get(endpointUnitById(fkValue));
      const unit = normalizeUnit(resUnit.data);
      setUnitData(unit);

      // 2. Si tiene dirección vinculada, cargarla
      if (unit.idDireccion && unit.idDireccion > 0) {
        const resDir = await axios.get(`${endpointBase}/${unit.idDireccion}`);
        setLinkedAddress(normalizeDir(resDir.data));
      } else {
        setLinkedAddress(null);
      }
    } catch (e) {
      console.error(e);
      // Solo lanzamos alerta si falló algo crítico
      if (e.response?.status !== 404) {
        triggerAlert(`Error al cargar la información de la unidad.`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fkValue) loadData();
  }, [fkValue]);

  const handleNew = () => {
    setFormMode('new');
    setEditData(null);
    setFormOpen(true);
  };

  const handleEdit = () => {
    if (!linkedAddress) return;
    setFormMode('edit');
    setEditData(linkedAddress);
    setFormOpen(true);
  };

  const handleUnlink = () => {
    setDeleteOpen(true);
  };

  const confirmUnlink = async () => {
    try {
      setLoading(true);
      // Solo desvinculamos de la unidad (no borramos la dirección del catálogo global)
      const payload = { ...unitData, idDireccion: 0 };
      await axios.put(endpointUnitPut, payload);
      await loadData();
      if (onUpdate) onUpdate(); // Notificar al catálogo principal
      setDeleteOpen(false);
    } catch (e) {
      console.error(e);
      triggerAlert('Error al desvincular la dirección.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData, mode) => {
    try {
      setLoading(true);
      let targetDirId = linkedAddress?.idDireccion;

      if (mode === 'new') {
        const res = await axios.post(endpointBase, formData);
        targetDirId = res?.data?.idDireccion ?? (typeof res?.data === 'number' ? res.data : null);

        // Vincular a la unidad
        if (targetDirId) {
          const payload = { ...unitData, idDireccion: targetDirId };
          await axios.put(endpointUnitPut, payload);
        }
      } else {
        await axios.put(endpointBase, formData);
      }

      setFormOpen(false);
      await loadData();
      if (onUpdate) onUpdate(); // Notificar al catálogo principal
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar la dirección.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mini-crud">
      <div className="mini-topbar">
        <h3 className="roles-title" style={{ margin: 0 }}>Gestión de Dirección</h3>
        {!linkedAddress && (
          <button className="btn-action" onClick={handleNew}>＋ Nueva Dirección</button>
        )}
      </div>

      <div className="roles-table-wrap">
        <div className="roles-table-scroller">
          <table className="roles-table">
            <thead>
              <tr>
                <th>Calle</th>
                <th>No. Ext.</th>
                <th>Colonia</th>
                <th>Municipio</th>
                <th>Estado</th>
                <th>CP</th>
                <th style={{ textAlign: 'center' }}>Editar</th>
                <th style={{ textAlign: 'center' }}>Desvincular</th>
              </tr>
            </thead>
            <tbody>
              {linkedAddress ? (
                <tr>
                  <td>{linkedAddress.calle}</td>
                  <td>{linkedAddress.numeroExterior}</td>
                  <td>{linkedAddress.colonia}</td>
                  <td>{linkedAddress.municipio}</td>
                  <td>{linkedAddress.estado}</td>
                  <td>{linkedAddress.codigoPostal}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button type="button" className="rk-iconbtn rk-iconbtn--edit" onClick={handleEdit}>
                      <img src={sigmaEdit} alt="" className="rk-ico" />
                    </button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button type="button" className="rk-iconbtn rk-iconbtn--del" onClick={handleUnlink} title="Desvincular de esta unidad">
                      <img src={sigmaDelete} alt="" className="rk-ico" />
                    </button>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No hay una dirección vinculada a esta unidad organizacional.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={formOpen}
        mode={formMode}
        data={editData}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        title="Desvincular Dirección"
        message="¿Estás seguro de que deseas desvincular esta dirección de la unidad? La dirección permanecerá en el catálogo global."
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmUnlink}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
